import type { MouseEvent } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconLinkOutline18 as LinkIcon,
} from "nucleo-ui-outline-18"

import { needsCascade } from "@productlane/lib/src/article-visibility"
import { deriveDocsUrlName } from "@productlane/lib/src/portal-slugs"
import { Button } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ArticleVisibility } from "./article-visibility"
import { PortalSlugField } from "@/components/shared/portal-slug-field"
import {
	VISIBILITY_MENU_WIDTH_CLASS,
	VISIBILITY_OPTION_ROW_CLASS,
	VisibilityOptionRow,
} from "@/components/shared/visibility-option-row"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { ArticleOldUrls } from "./article-old-urls"
import {
	ARTICLE_VISIBILITY_OPTIONS,
	getArticleVisibilityLabel,
	VISIBILITY_ICONS,
} from "./article-visibility"
import { liveOldUrlNames } from "./folder-redirects"

const CONFIRMATION: Record<ArticleVisibility, string> = {
	PUBLIC: "Article is now public",
	UNLISTED: "Article is now agent only",
	INTERNAL: "Article is now internal",
}

export interface ArticleUrlProps {
	urlName: string
	previousUrlNames?: string[] | null
	published: boolean
	title: string
	helpCenterGroupId: string | null | undefined
}

export const ArticleVisibilityButton = ({
	articleId,
	visibility,
	article,
}: {
	articleId: string
	visibility: ArticleVisibility
	article?: ArticleUrlProps
}) => {
	const zero = useZero()
	const { role } = useRole()
	const canEdit = role !== "VIEWER"
	const [subpages] = useQuery(queries.helpCenterArticleSubpages(articleId))
	const [groups] = useQuery(queries.helpCenterArticlesWithGroups())

	const setVisibility = async (next: ArticleVisibility) => {
		if (next === visibility) return
		// The mutator hides the branch along with the parent, so undo has to know
		// which subpages it took with it, and what each of them was before.
		const cascaded = (subpages ?? [])
			.filter((s) => needsCascade(next, s.visibility))
			.map((s) => ({ id: s.id, visibility: s.visibility }))
		await mutateOk(
			zero.mutate(
				mutators.helpCenterArticles.update({ id: articleId, visibility: next }),
			),
		)
		toast.success({
			icon: VISIBILITY_ICONS[next],
			// Kept short and without the article title so it never wraps.
			title: CONFIRMATION[next],
			description:
				cascaded.length > 0
					? `${cascaded.length} nested article${cascaded.length === 1 ? "" : "s"} followed.`
					: undefined,
			action: {
				label: "Undo",
				callback: () => {
					void mutateOk(
						zero.mutate(
							mutators.helpCenterArticles.update({
								id: articleId,
								visibility,
							}),
						),
					)
					for (const subpage of cascaded) {
						void mutateOk(
							zero.mutate(
								mutators.helpCenterArticles.update({
									id: subpage.id,
									visibility: subpage.visibility,
								}),
							),
						)
					}
				},
			},
		})
		analytics.track("set-helpcenterarticle-visibility", {
			id: articleId,
			visibility: next,
		})
	}

	const groupName = (groups ?? []).find(
		(g) => g.id === article?.helpCenterGroupId,
	)?.name
	const derivedUrlName = article
		? deriveDocsUrlName(groupName, article.title)
		: undefined
	// Publishing is the freeze point: until then the slug still tracks the title.
	const isFrozen = !!article?.published
	const urlValue = article
		? isFrozen
			? article.urlName
			: (derivedUrlName ?? article.urlName)
		: ""
	const oldUrlNames = article
		? liveOldUrlNames({
				published: article.published,
				visibility,
				previousUrlNames: article.previousUrlNames,
			})
		: []

	const saveUrlName = async (next: string) => {
		const result = zero.mutate(
			mutators.helpCenterArticles.setUrlName({ id: articleId, urlName: next }),
		)
		try {
			await mutateOk(result)
			await mutateOk(result, "server")
		} catch (error) {
			toast.info({
				icon: LinkIcon,
				title: error instanceof Error ? error.message : "That URL is not valid",
				description: "Choose a different URL.",
			})
			return
		}
		toast.success({
			icon: LinkIcon,
			title: isFrozen ? "URL changed. The old URL redirects." : "URL set.",
		})
		analytics.track("set-helpcenterarticle-url", { id: articleId })
	}

	return (
		<div
			role="presentation"
			onClick={(e: MouseEvent) => {
				e.preventDefault()
				e.stopPropagation()
			}}
		>
			<DropdownMenu
				trigger={
					<Button
						variant="outline"
						className="gap-2"
						Icon={VISIBILITY_ICONS[visibility]}
						iconRight={ChevronDown}
						disabled={!canEdit}
					>
						{getArticleVisibilityLabel(visibility)}
					</Button>
				}
				contentProps={{ side: "bottom", align: "end" }}
				className={VISIBILITY_MENU_WIDTH_CLASS}
			>
				{ARTICLE_VISIBILITY_OPTIONS.map((option) => {
					const Icon = VISIBILITY_ICONS[option.value]
					return (
						<div key={option.value}>
							<DropdownMenuItem
								className={VISIBILITY_OPTION_ROW_CLASS}
								onSelect={() => void setVisibility(option.value)}
							>
								<VisibilityOptionRow
									mark={
										<Icon className="h-3.5 w-3.5 flex-none text-tertiary" />
									}
									label={option.label}
									description={option.description}
									isSelected={option.value === visibility}
								/>
							</DropdownMenuItem>
							{option.value === "PUBLIC" && article && (
								<PortalSlugField
									prefix="/docs/"
									value={urlValue}
									isFrozen={isFrozen}
									derived={derivedUrlName}
									disabled={!canEdit}
									onSave={saveUrlName}
								/>
							)}
							{option.value === "PUBLIC" && oldUrlNames.length > 0 && (
								<ArticleOldUrls
									articleId={articleId}
									urlNames={oldUrlNames}
									canEdit={canEdit}
								/>
							)}
						</div>
					)
				})}
			</DropdownMenu>
		</div>
	)
}
